'use client';

import React, { useState, useEffect } from 'react';
import { trackCTAClick } from '@/lib/analytics';

interface KSeFPromoBannerProps {
  className?: string;
}

interface BannerSettings {
  banner_title: string;
  banner_description: string;
  button_text: string;
  button_url: string;
  is_active: boolean;
}

const DEFAULT_SETTINGS: BannerSettings = {
  banner_title: 'Spokój z KSeF? Wdrażamy w 48h.',
  banner_description: 'Pamiętaj: od 1 kwietnia 2026 obowiązkowe wystawianie faktur w KSeF. Bądź gotowy.',
  button_text: 'Kup wdrożenie KSeF – 299 PLN',
  button_url: '#pakiety',
  is_active: true,
};

const BASESCAN_BANNER = {
  banner_title: 'BaseScan – sprawdź zanim kupisz',
  banner_description: 'Skanuj kody EAN, sprawdzaj ceny na Allegro i wyliczaj marżę w sekundę. Integracja z BaseLinker w planie Pro.',
  button_text: 'Pobierz na Google Play',
  button_url: 'https://play.google.com/store/apps/details?id=com.emt.basescan',
};

export default function KSeFPromoBanner({ className = '' }: KSeFPromoBannerProps) {
  const [bannerSettings, setBannerSettings] = useState<BannerSettings>(DEFAULT_SETTINGS);
  const [loaded, setLoaded] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const fetchBannerSettings = async () => {
      try {
        const response = await fetch('/api/admin/ksef-banner');
        if (response.ok) {
          const result = await response.json();
          if (result.data) {
            setBannerSettings(result.data);
          }
        }
      } catch (err) {
        console.error('Error fetching banner settings:', err);
      } finally {
        setLoaded(true);
      }
    };

    fetchBannerSettings();
  }, []);

  // Rotate banners every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveIndex((prev) => (prev === 0 ? 1 : 0));
        setIsTransitioning(false);
      }, 300);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleCTAClick = (banner: typeof BASESCAN_BANNER | BannerSettings) => {
    const buttonText = banner.button_text;
    const url = banner.button_url;

    trackCTAClick(buttonText, 'promo_banner');

    if (url.startsWith('#')) {
      const targetId = url.slice(1);
      const target = document.getElementById(targetId);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      const onlinePackage = document.getElementById('package-online');
      if (onlinePackage) {
        onlinePackage.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      const pakietySection = document.getElementById('pakiety');
      if (pakietySection) {
        pakietySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Don't render if KSeF banner is inactive (only after data is loaded)
  if (loaded && !bannerSettings.is_active) {
    return null;
  }

  const banners = [bannerSettings, BASESCAN_BANNER];
  const currentBanner = banners[activeIndex];
  const isBaseScan = activeIndex === 1;

  return (
    <div className={`w-full ${className}`}>
      <div
        className={`w-full transition-colors duration-500 ${
          isBaseScan
            ? 'bg-gradient-to-r from-[#0a1628] via-[#0d2040] to-[#0a1628]'
            : 'bg-gradient-to-r from-[#0f172a] via-[#1e293b] to-[#0f172a]'
        }`}
        style={{ padding: '60px 0' }}>

        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mb-6">
            {banners.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setIsTransitioning(true);
                  setTimeout(() => {
                    setActiveIndex(i);
                    setIsTransitioning(false);
                  }, 300);
                }}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  i === activeIndex ? 'bg-white w-6' : 'bg-white/40'
                }`}
                aria-label={`Baner ${i + 1}`}
              />
            ))}
          </div>

          <div
            className={`grid grid-cols-1 md:grid-cols-[2fr_auto] items-center gap-8 md:gap-16 transition-opacity duration-300 ${
              isTransitioning ? 'opacity-0' : 'opacity-100'
            }`}>

            {/* Left side: Text content */}
            <div className="text-left">
              <h2
                className="font-bold text-white text-lg md:text-[1.75rem] whitespace-nowrap"
                style={{ lineHeight: '1.2', marginBottom: '16px' }}>
                {currentBanner.banner_title}
              </h2>
              <p
                className="text-sm md:text-sm text-white"
                style={{
                  marginTop: '12px',
                  lineHeight: '1.5',
                  opacity: 0.85,
                  minHeight: '3rem'
                }}>
                {currentBanner.banner_description}
              </p>
            </div>

            {/* Right side: CTA Button */}
            <div className="md:flex-shrink-0 w-full md:w-auto">
              <button
                onClick={() => handleCTAClick(currentBanner)}
                className={`inline-flex items-center justify-center text-white font-bold text-sm md:text-lg rounded-lg transition-all duration-300 ease-out hover:scale-105 active:scale-95 w-full md:w-auto whitespace-nowrap ${
                  isBaseScan
                    ? 'bg-[#00a651] hover:bg-[#00b85a] hover:shadow-[0_8px_30px_rgba(0,166,81,0.4)]'
                    : 'bg-[#ff6a00] hover:bg-[#ff7a1a] hover:shadow-[0_8px_30px_rgba(255,106,0,0.4)]'
                }`}
                style={{
                  fontFamily: 'Inter, sans-serif',
                  padding: '16px 24px'
                }}>
                {currentBanner.button_text}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}